import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { COUNTRY_VALUES } from '@utils/address';
import * as Yup from 'yup';

import { Button } from 'components/button/Button';
import { InputField } from 'components/input/InputField';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { Country, Language } from 'src/types/enums';
import { customerPortalUpdateCustomer } from '../../customer/models';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { formatVatNumber } from '../../../utils/vat-number';
import { getDisplayError } from '../../../utils/get-display-error';
import { useCustomerPortalCustomer } from '../CustomerPortalCustomerContext';
import { AutocompletePostalcode } from '../../location/components/AutocompletePostalcode';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { PageHeader } from '../../../components/PageHeader';
import { useTranslation } from '../../../contexts/translation-context';
import { DataField } from '../../../components/DataField';
import { PageHeading } from 'components/PageHeading';
import { captureException } from '@sentry/react';

const updateCustomerSchema = Yup.object().shape({
  street: Yup.string().min(1, 'errors.required').required('errors.required'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'errors.required').required('errors.required'),
  postalCode: Yup.string().min(1, 'errors.required').required('errors.required'),
  country: Yup.mixed().nullable().required('errors.required'),
  language: Yup.mixed().nullable().required('errors.required'),
});

export const CustomerPortalUpdateCustomerDetailsPage = () => {
  const { customer, refreshData } = useCustomerPortalCustomer();
  const { i18n } = useTranslation();

  const initialValues = useMemo(() => {
    return {
      street: customer.street,
      streetNumber: customer.streetNumber ?? '',
      city: customer.city,
      postalCode: customer.postalCode,
      country: COUNTRY_VALUES.find((v) => v.key === customer.country)!,
      language: LANGUAGE_VALUES.find((v) => v.key === customer.language)!,
    };
  }, [customer]);

  return (
    <div>
      <PageHeader title={i18n('customerPortal.businessInformation.updateBusinessInformation')} />

      <PageHeading
        noMarginBottom
        leftSide={<Breadcrumb currentItem={i18n('customerPortal.businessInformation.businessInformation')} />}
      />

      <div className="grid gap-4 p-7">
        <div className="card flex flex-col gap-4 p-5">
          <DataField title={i18n('customerPortal.businessInformation.name')}>{customer.name}</DataField>

          <div className="grid grid-cols-2 gap-4">
            <DataField title={i18n('customerPortal.businessInformation.vatNumber')}>
              {formatVatNumber(customer.vatNumber)}
            </DataField>
            <DataField title={i18n('customerPortal.businessInformation.businessNumber')}>
              {formatVatNumber(customer.companyNumber)}
            </DataField>
          </div>

          <div className="h-px w-full bg-border" />

          <Formik
            initialValues={initialValues}
            validationSchema={updateCustomerSchema}
            onSubmit={async (newValues) => {
              try {
                const patch: Partial<typeof newValues> = diff(initialValues, newValues);
                const updateData = {
                  ...patch,
                  country: patch.country ? (patch.country.key as Country) : undefined,
                  language: patch.language ? (patch.language.key as Language) : undefined,
                };
                if (Object.values(updateData).length) {
                  await customerPortalUpdateCustomer(customer.id, updateData);
                  await refreshData();
                }
                toast.success(i18n('customerPortal.businessInformation.informationChanged'));
              } catch (err: any) {
                captureException(err);
                toast.error(
                  i18n('customerPortal.businessInformation.informationNotChanged', {
                    error: getDisplayError(err),
                  }),
                );
              }
            }}
          >
            {({ handleSubmit, isSubmitting }) => {
              return (
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="flex gap-4">
                    <div className="flex-4">
                      <InputField labelText={i18n('customerPortal.locations.street')} type="text" name="street" />
                    </div>
                    <div className="flex-1">
                      <InputField labelText={i18n('customerPortal.locations.number')} type="text" name="streetNumber" />
                    </div>
                  </div>
                  <AutocompletePostalcode />
                  <div className="max-w-[320px]">
                    <SimpleSelectField
                      labelText={i18n('customerPortal.locations.language')}
                      items={LANGUAGE_VALUES}
                      name="language"
                    />
                  </div>

                  <div>
                    <Button
                      type="submit"
                      color="primary"
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      iconLeft={<PlusIcon className="button-icon" />}
                    >
                      {i18n('customerPortal.businessInformation.saveChanges')}
                    </Button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </div>

        <div className="text-xs text-fg-muted">{i18n('customerPortal.businessInformation.requestChanges')}</div>
      </div>
    </div>
  );
};
